import * as React from 'react';
import { MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';
import { MapView } from '@ooh/ui/components/map-view';
import type { MapPin as MapViewPin } from '@ooh/ui/components/map-view';
import { formatCoordinatePair } from '@ooh/ui/lib/media-unit-formatters';

const DETAIL_LOCATION_CATEGORY_COLORS = { unit: '#3b82f6' } as const;

function buildDetailLocationPin(pinId: string, latitude: number, longitude: number): MapViewPin {
  return {
    id: pinId,
    latitude,
    longitude,
    category: 'unit',
  };
}

interface DetailLocationCardProps {
  title: string;
  latitude: number | null;
  longitude: number | null;
  pinId: string;
  footerAction?: React.ReactNode;
  /** Translated label shown above `description` (e.g. "Opis lokalizacji"). */
  descriptionLabel?: string;
  /** Client-visible free-text location description. Rendered only when non-empty. */
  description?: string | null;
}

export function DetailLocationCard({
  title,
  latitude,
  longitude,
  pinId,
  footerAction,
  descriptionLabel,
  description,
}: DetailLocationCardProps) {
  const hasCoordinates = latitude != null && longitude != null;
  const pins = hasCoordinates ? [buildDetailLocationPin(pinId, latitude, longitude)] : [];

  return (
    <Card data-testid="map-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-[15px]">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {description ? (
          <div className="mb-3" data-testid="field-location-description">
            {descriptionLabel ? (
              <p className="mb-0.5 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                {descriptionLabel}
              </p>
            ) : null}
            <p className="text-sm text-foreground">{description}</p>
          </div>
        ) : null}
        <div className="relative mb-3 aspect-[2/1] overflow-hidden rounded-lg bg-muted">
          {hasCoordinates ? (
            <MapView
              pins={pins}
              initialCenter={[latitude, longitude]}
              initialZoom={13}
              categoryColors={DETAIL_LOCATION_CATEGORY_COLORS}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <MapPin className="h-8 w-8 text-muted-foreground/50" />
            </div>
          )}

          {hasCoordinates ? (
            <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded bg-background/80 px-2 py-1 backdrop-blur-sm">
              <MapPin className="h-3.5 w-3.5 text-primary" />
              <span className="font-mono text-2xs text-foreground">
                {formatCoordinatePair(latitude, longitude)}
              </span>
            </div>
          ) : null}
        </div>

        {footerAction}
      </CardContent>
    </Card>
  );
}
